import { Icon, Tooltip } from '@mui/material'
import { Instance } from '@popperjs/core'
import React, { ReactElement, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { FILLER_EVENT_ID } from '../../BarCalendar.helper'
import { Event } from '../../BarCalendar.types'
import EventTooltip from '../BCEventTooltip/BCEventTooltip'
import styles from './BCEventBar.module.less'

type EventBarProps = {
  event: Event
  width: string
  onClick?: (setting: Event) => void
  highlighted?: boolean
}

export default function EventBar({ event, width, onClick, highlighted = false }: EventBarProps): ReactElement {
  const { t } = useTranslation()
  // Make tooltip follow cursor.
  const positionRef = useRef<{ x: number; y: number }>({
    x: 0,
    y: 0,
  })
  const popperRef = useRef<Instance>(null)
  const areaRef = useRef<HTMLDivElement>(null)
  const rafRef = useRef<number | null>(null)

  // Coalesce tooltip-follows-cursor updates to one per frame. Updating the popper on every
  // mousemove caused heavy layout thrash on dense calendars (70+ bars) and a constantly
  // repositioning popper under the cursor — the closest thing to the "continuous animation"
  // the e2e suite struggled with.
  const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>): void => {
    positionRef.current = { x: event.clientX, y: event.clientY }

    if (rafRef.current != null) {
      return
    }

    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null
      popperRef.current?.update()
    })
  }

  useEffect(() => {
    return () => {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current)
      }
    }
  }, [])

  const name = event.name || 'NO NAME'
  const createdBySource = typeof event.meta?.createdBySource === 'string' ? event.meta.createdBySource : undefined
  let styleClass = styles.EventBar
  if (typeof event.id === 'string' && event.id.includes('filler')) {
    styleClass = styles.EventBar_Filler
  }
  let turnicatedStyle = ''
  if (event.isTurnicatedStart && event.isTurnicatedEnd) {
    turnicatedStyle = styles.EventBar_TurnicatedBoth
  } else if (event.isTurnicatedStart) {
    turnicatedStyle = styles.EventBar_TurnicatedStart
  } else if (event.isTurnicatedEnd) {
    turnicatedStyle = styles.EventBar_TurnicatedEnd
  }

  const content = (
    <div
      onClick={() => {
        if (onClick) {
          onClick(event)
        }
      }}
      className={`${styleClass} ${turnicatedStyle} ${
        event.isExcludedSettingForOptimization ? styles.EventBar_ExcludedFromPriorityLayer : ''
      } ${highlighted ? styles.EventBar_Highlighted : ''}`}
      style={{
        backgroundColor: event.isExcludedSettingForOptimization ? 'transparent' : event.color,
        borderColor: event.isExcludedSettingForOptimization ? event.color : undefined,
        color: event.isExcludedSettingForOptimization ? undefined : event.textColor,
        minWidth: width,
        width: width,
        maxWidth: width,
      }}
      data-testid="eventbar-title" // e2e: Robot/Playwright calendar bar selector — do not remove
      data-event-name={name} // e2e: exact-match event selector — do not remove
      data-event-id={event.id} // e2e: stable id selector (event id / setting id) — do not remove
      data-event-reason={typeof event.meta?.reason === 'string' ? event.meta.reason : undefined} // e2e: settings-bar reason selector — do not remove
    >
      <div className={styles.EventBar_Title}>
        {t(name)}
        {event.icons?.operationalEvent && (
          <Tooltip title={t('Included in an event')} arrow>
            <Icon
              fontSize="small"
              className={`fal fa-calendar-week ${styles.EventBar_Icon}`}
              data-testid="event-link-icon" // e2e: event-linked marker icon — do not remove
            />
          </Tooltip>
        )}
        {event.icons?.timeSeries && (
          <Tooltip title={t('Time series')} arrow>
            <Icon fontSize="small" className={`fal fa-clock ${styles.EventBar_Icon}`} />
          </Tooltip>
        )}
        {event.icons?.excludedFromPrioLayer && (
          <Tooltip
            title={t('Excluded from {{priorityLayer}}', {
              priorityLayer: event.meta?.excludedPriorityLabel,
            })}
            arrow
          >
            <Icon fontSize="small" className={`fal fa-map-marker-times ${styles.EventBar_Icon}`} />
          </Tooltip>
        )}
        {event.icons?.createdBySource && createdBySource && createdBySource !== 'app' && (
          <Tooltip
            title={t('Setting created by: {{source}}', {
              source: t(createdBySource),
            })}
            arrow
          >
            <Icon fontSize="small" className={`fal fa-upload ${styles.EventBar_Icon}`} />
          </Tooltip>
        )}
      </div>
    </div>
  )

  // Filler should not have tooltip.
  if (event.id == FILLER_EVENT_ID) {
    return content
  }

  return (
    <Tooltip
      placement="top"
      classes={{ tooltip: styles.EventBar_Tooltip, popper: styles.Popper }}
      title={<EventTooltip event={event} />}
      slotProps={{
        popper: {
          popperRef,
          anchorEl: {
            getBoundingClientRect: () => {
              return new DOMRect(positionRef.current.x, areaRef.current?.getBoundingClientRect().y, 0, 0)
            },
          },
        },
      }}
    >
      <div ref={areaRef} onMouseMove={handleMouseMove} className={styles.EventBar__outline_none}>
        {content}
      </div>
    </Tooltip>
  )
}
